/**
 * Shuttle GA DataLayer Events
 * 
 * Handles Google Analytics event tracking for shuttle booking funnel
 * 
 * @package A2A_Theme
 */

class ShuttleGAEvents {
    constructor() {
        // Initialize dataLayer if it doesn't exist
        window.dataLayer = window.dataLayer || [];
    }

    /**
     * Push event to dataLayer
     */
    pushEvent(eventName, parameters = {}) {
        const eventData = {
            event: eventName,
            ...parameters
        };
        
        window.dataLayer.push(eventData);
    }

    /**
     * Row 4 & 6: Search pressed (Step 1 form submit)
     * Triggered when user submits search form
     */
    trackSearchPressed(bookingData) {
        this.pushEvent('search_pressed', {
            pickup_location: bookingData.from?.label || '',
            dropoff_location: bookingData.to?.label || '',
            transfer_type: bookingData.transferType || 'one-way', // 'return' or 'one-way'
            adults: bookingData.passengers?.adults || 0,
            children: bookingData.passengers?.children || 0,
            vehicle_type: 'shuttle'
        });
    }

    /**
     * Row 7: Book outbound (Step 2 - select outbound route)
     * Triggered when user selects outbound transfer time
     */
    trackBookOutbound(departureTime) {
        this.pushEvent('book_outbound', {
            time: departureTime,
            vehicle_type: 'shuttle'
        });
    }

    /**
     * Row 8: Book return (Step 2 - select return route)
     * Triggered when user selects return transfer time
     */
    trackBookReturn(departureTime) {
        this.pushEvent('book_return', {
            time: departureTime,
            vehicle_type: 'shuttle'
        });
    }

    /**
     * Row 12: Fare chosen (Step 3 - select fare tier)
     * Triggered when user selects a fare option
     */
    trackFareChosen(fareName) {
        this.pushEvent('fare_choosen', {
            fare_name: fareName, // 'basic', 'skier', or 'pro'
            vehicle_type: 'shuttle'
        });
    }

    /**
     * Row 13: Luggage chosen (Step 3 - add luggage)
     * Triggered when user changes luggage quantities
     */
    trackLuggageChosen(checkedIn, ski) {
        this.pushEvent('luggage_choosen', {
            checked_in: checkedIn,
            ski: ski
        });
    }

    /**
     * Row 14: Child seat chosen (Step 3 - add child seats)
     * Triggered when user changes child seat quantities
     */
    trackChildSeatChosen(childSeat, booster) {
        this.pushEvent('child_seat_choosen', {
            child_seat: childSeat,
            booster: booster
        });
    }

    /**
     * Row 15: Pay clicked (Step 3 - continue to payment)
     * Triggered when user clicks "Continue to Payment" button
     */
    trackPayClicked(checkoutData) {
        this.pushEvent('pay_clicked', {
            fare_name: checkoutData.fareCondition || 'basic',
            checked_in: checkoutData.luggage?.additional_bag || 0,
            ski: checkoutData.luggage?.ski_bag || 0,
            child_seat: checkoutData.childSeats?.regular || 0,
            booster: checkoutData.childSeats?.booster || 0,
            vehicle_type: 'shuttle'
        });
    }

    /**
     * Row 16: Pay clicked with payment method (Step 4 - payment selection)
     * Triggered when user clicks "Pay Now" button after selecting payment method
     */
    trackPayClickedWithMethod(paymentMethod) {
        this.pushEvent('pay_clicked', {
            payment_method: paymentMethod // 'card' or 'paypal'
        });
    }

    /**
     * Row 19: Purchase (Thank You page - successful payment)
     * Triggered on thank you page after successful payment
     * 
     * @param {Object} orderData - Order details from backend
     * @param {string} orderData.transaction_id - Order ID
     * @param {number} orderData.value - Total amount
     * @param {string} orderData.currency - Currency code (EUR, GBP, etc.)
     * @param {Object} orderData.items - Optional: Array of purchased items
     */
    trackPurchase(orderData) {
        const purchaseEvent = {
            event: 'purchase',
            transaction_id: orderData.transaction_id,
            value: parseFloat(orderData.value),
            currency: orderData.currency || 'EUR',
            vehicle_type: 'shuttle'
        };

        // Add optional ecommerce data if provided
        if (orderData.items && orderData.items.length > 0) {
            purchaseEvent.items = orderData.items;
        }

        this.pushEvent('purchase', purchaseEvent);
    }

    /**
     * ========================================
     * ENHANCED ECOMMERCE TRACKING METHODS (UA FORMAT)
     * ========================================
     */

    /**
     * Get user ID from authentication cookie
     * Returns the numeric user ID from the decoded cookie data
     * @returns {number|null} User ID or null if not logged in
     */
    getUserId() {
        try {
            // Determine cookie name based on environment
            const cookieName = window.location.hostname.includes('test') ? '_a2alu_test' : '_a2alu';
            
            // Parse cookies
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name === cookieName) {
                    try {
                        const cookieData = JSON.parse(decodeURIComponent(value));
                        
                        // Decode the base64 'data' field to get user details
                        if (cookieData.data) {
                            const userData = JSON.parse(atob(cookieData.data));
                            // Return the numeric user ID
                            return userData.id || null;
                        }
                    } catch (e) {
                        // Failed to parse cookie
                        return null;
                    }
                }
            }
        } catch (e) {
            // Error getting user ID
            return null;
        }
        
        return null;
    }

    /**
     * Format date as DD/MM/YYYY
     * Input format: "DD.MM.YY, HH:mm" or "DD.MM.YY"
     */
    formatDate(dateString) {
        if (!dateString) return '';
        
        // Extract date part (before comma if present)
        const datePart = dateString.split(',')[0].trim();
        
        // Parse DD.MM.YY format
        const parts = datePart.split('.');
        if (parts.length === 3) {
            const day = parts[0];
            const month = parts[1];
            const year = parts[2];
            // Convert YY to YYYY (assuming 20xx)
            const fullYear = year.length === 2 ? `20${year}` : year;
            return `${day}/${month}/${fullYear}`;
        }
        
        return '';
    }

    /**
     * Format date and time as DD.MM.YY HH:mm
     * Input dateString format: "DD.MM.YY, HH:mm" or "DD.MM.YY"
     * Input timeString: "HH:mm" (departure time from route)
     */
    formatDateTime(dateString, timeString) {
        if (!dateString) return '';
        
        // Extract date part (before comma if present)
        const datePart = dateString.split(',')[0].trim();
        
        // Use the provided timeString (departure time from route)
        return `${datePart} ${timeString || ''}`.trim();
    }

    /**
     * Build UA Enhanced Ecommerce products array from booking data
     * If orderData is provided, uses calculated totals in EUR (includes all extras)
     * Otherwise uses base route prices
     * 
     * @param {Object} bookingData - Booking data
     * @param {number} routeId - Route ID (optional)
     * @param {Object} orderData - Order data with calculated totals (optional)
     * @returns {Array} Array of products in UA format
     */
    buildProducts(bookingData, routeId = null, orderData = null) {
        const products = [];
        
        // Add outbound route
        if (bookingData.selectedRoutes?.outbound) {
            const route = bookingData.selectedRoutes.outbound;
            const routeName = `${bookingData.from?.label || 'Unknown'} -- ${bookingData.to?.label || 'Unknown'}`;
            const variant = this.formatDateTime(bookingData.outboundDate, bookingData.outboundTime);
            const dimension1 = this.formatDate(bookingData.outboundDate);
            
            // Use calculated total in EUR if available (includes all extras)
            let totalPrice = 0;
            if (orderData?.calculatedTotals?.outbound?.price_eur) {
                totalPrice = orderData.calculatedTotals.outbound.price_eur;
            } else {
                // Fallback to base price
                const passengers = (bookingData.passengers?.adults || 0) + (bookingData.passengers?.children || 0);
                const pricePerPerson = route.price?.amount || 0;
                totalPrice = pricePerPerson * passengers;
            }
            
            products.push({
                name: routeName,
                id: routeId || route.routeId || route.id || 0,
                price: totalPrice.toFixed(2),
                brand: 'Alps2Alps',
                category: 'Shuttle',
                variant: variant,
                quantity: 1,
                dimension1: dimension1
            });
        }
        
        // Add return route if applicable
        if (bookingData.transferType === 'return' && bookingData.selectedRoutes?.return) {
            const route = bookingData.selectedRoutes.return;
            const routeName = `${bookingData.to?.label || 'Unknown'} -- ${bookingData.from?.label || 'Unknown'}`;
            const variant = this.formatDateTime(bookingData.returnDate, bookingData.returnTime);
            const dimension1 = this.formatDate(bookingData.returnDate);
            
            // Use calculated total in EUR if available (includes all extras)
            let totalPrice = 0;
            if (orderData?.calculatedTotals?.return?.price_eur) {
                totalPrice = orderData.calculatedTotals.return.price_eur;
            } else {
                // Fallback to base price
                const passengers = (bookingData.passengers?.adults || 0) + (bookingData.passengers?.children || 0);
                const pricePerPerson = route.price?.amount || 0;
                totalPrice = pricePerPerson * passengers;
            }
            
            products.push({
                name: routeName,
                id: routeId || route.routeId || route.id || 0,
                price: totalPrice.toFixed(2),
                brand: 'Alps2Alps',
                category: 'Shuttle',
                variant: variant,
                quantity: 1,
                dimension1: dimension1
            });
        }
        
        return products;
    }

    /**
     * STEP 2: Product Detail View (Route Selection)
     * Triggered when user views available routes
     * 
     * @param {Object} bookingData - Booking data with selected routes
     * @param {Array} availableRoutes - Array of available routes to display
     */
    trackProductDetail(bookingData, availableRoutes = []) {
        const products = [];
        
        // Determine if we're showing return routes
        const isReturnSelection = bookingData.currentSelection === 'return';
        
        // Determine which date to use based on current selection state
        const currentDate = isReturnSelection 
            ? bookingData.returnDate 
            : bookingData.outboundDate;
        
        // Build products from available routes
        if (availableRoutes && availableRoutes.length > 0) {
            availableRoutes.forEach(route => {
                const passengers = (bookingData.passengers?.adults || 0) + (bookingData.passengers?.children || 0);
                const pricePerPerson = route.price?.amount || 0;
                const totalPrice = pricePerPerson * passengers;
                
                // Swap from/to for return trips
                const routeName = isReturnSelection
                    ? `${bookingData.to?.label || 'Unknown'} -- ${bookingData.from?.label || 'Unknown'}`
                    : `${bookingData.from?.label || 'Unknown'} -- ${bookingData.to?.label || 'Unknown'}`;
                
                // Use the date from bookingData, not from route
                const variant = this.formatDateTime(currentDate, route.departureTime);
                const dimension1 = this.formatDate(currentDate);
                
                products.push({
                    name: routeName,
                    id: route.routeId || 0,
                    price: totalPrice.toFixed(2),
                    brand: 'Alps2Alps',
                    category: 'Shuttle',
                    variant: variant,
                    quantity: 1,
                    dimension1: dimension1
                });
            });
        }
        
        window.dataLayer.push({
            event: 'detail',
            ecommerce: {
                detail: {
                    products: products
                }
            }
        });
    }

    /**
     * STEP 3: Checkout Step 1 (Login and transfer options)
     * Triggered when checkout page loads
     * 
     * @param {Object} bookingData - Booking data
     * @param {number|null} orderId - Order ID from backend (optional, may not exist on page load)
     * @param {number} routeId - Route ID (optional)
     */
    trackCheckoutStep1(bookingData, orderId = null, routeId = null) {
        const products = this.buildProducts(bookingData, routeId);
        
        const eventData = {
            event: 'checkout',
            ecommerce: {
                checkout: {
                    actionField: {
                        step: 1
                    },
                    products: products
                }
            }
        };
        
        // Add order_id only if it exists
        if (orderId) {
            eventData.order_id = orderId;
        }
        
        window.dataLayer.push(eventData);
    }

    /**
     * STEP 4: Checkout Step 2 (Payment info)
     * Triggered when payment page loads
     * 
     * @param {Object} bookingData - Booking data
     * @param {number} orderId - Order ID from backend
     * @param {number} routeId - Route ID (optional)
     * @param {Object} orderData - Order data with calculated totals (optional)
     */
    trackCheckoutStep2(bookingData, orderId, routeId = null, orderData = null) {
        const userId = this.getUserId();
        // Pass orderData to buildProducts to use full EUR prices with extras
        const products = this.buildProducts(bookingData, routeId, orderData);
        
        const eventData = {
            event: 'checkout',
            order_id: orderId,
            ecommerce: {
                checkout: {
                    actionField: {
                        step: 2
                    },
                    products: products
                }
            }
        };
        
        // Add userId if available
        if (userId) {
            eventData.userId = userId;
        }
        
        window.dataLayer.push(eventData);
    }

    /**
     * Build products for purchase event using calculated totals in EUR
     * This includes all extras, fare upgrades, etc.
     * Always uses EUR prices regardless of payment currency
     * 
     * @param {Object} bookingData - Booking data
     * @param {number} routeId - Route ID (optional)
     * @param {Object} orderData - Order data with calculated totals
     * @returns {Array} Array of products with full transfer prices in EUR
     */
    buildPurchaseProducts(bookingData, routeId = null, orderData = null) {
        const products = [];
        
        // Add outbound transfer
        if (bookingData.selectedRoutes?.outbound) {
            const route = bookingData.selectedRoutes.outbound;
            const routeName = `${bookingData.from?.label || 'Unknown'} -- ${bookingData.to?.label || 'Unknown'}`;
            const variant = this.formatDateTime(bookingData.outboundDate, bookingData.outboundTime);
            const dimension1 = this.formatDate(bookingData.outboundDate);
            
            // Use calculated total in EUR if available (includes all extras)
            // ALWAYS use price_eur for GA tracking
            let totalPrice = 0;
            if (orderData?.calculatedTotals?.outbound?.price_eur) {
                totalPrice = orderData.calculatedTotals.outbound.price_eur;
            } else {
                // Fallback to base price (assumes EUR)
                const passengers = (bookingData.passengers?.adults || 0) + (bookingData.passengers?.children || 0);
                const pricePerPerson = route.price?.amount || 0;
                totalPrice = pricePerPerson * passengers;
            }
            
            products.push({
                name: routeName,
                id: routeId || route.routeId || route.id || 0,
                price: totalPrice.toFixed(2),
                brand: 'Alps2Alps',
                category: 'Shuttle',
                variant: variant,
                quantity: 1,
                dimension1: dimension1
            });
        }
        
        // Add return transfer if applicable
        if (bookingData.transferType === 'return' && bookingData.selectedRoutes?.return) {
            const route = bookingData.selectedRoutes.return;
            const routeName = `${bookingData.to?.label || 'Unknown'} -- ${bookingData.from?.label || 'Unknown'}`;
            const variant = this.formatDateTime(bookingData.returnDate, bookingData.returnTime);
            const dimension1 = this.formatDate(bookingData.returnDate);
            
            // Use calculated total in EUR if available (includes all extras)
            // ALWAYS use price_eur for GA tracking
            let totalPrice = 0;
            if (orderData?.calculatedTotals?.return?.price_eur) {
                totalPrice = orderData.calculatedTotals.return.price_eur;
            } else {
                // Fallback to base price (assumes EUR)
                const passengers = (bookingData.passengers?.adults || 0) + (bookingData.passengers?.children || 0);
                const pricePerPerson = route.price?.amount || 0;
                totalPrice = pricePerPerson * passengers;
            }
            
            products.push({
                name: routeName,
                id: routeId || route.routeId || route.id || 0,
                price: totalPrice.toFixed(2),
                brand: 'Alps2Alps',
                category: 'Shuttle',
                variant: variant,
                quantity: 1,
                dimension1: dimension1
            });
        }
        
        return products;
    }

    /**
     * STEP 5: Purchase (Payment successful)
     * Triggered on thank you page after successful payment
     * 
     * @param {Object} bookingData - Booking data
     * @param {number} orderId - Order ID
     * @param {string} orderReference - Order reference (e.g., "A2A245150")
     * @param {number} revenue - Total revenue (includes all extras)
     * @param {number} routeId - Route ID (optional)
     * @param {Object} orderData - Order data with calculated totals (optional)
     */
    trackPurchaseUA(bookingData, orderId, orderReference, revenue, routeId = null, orderData = null) {
        const userId = this.getUserId();
        // Use purchase-specific product builder that includes calculated totals
        const products = this.buildPurchaseProducts(bookingData, routeId, orderData);
        
        const eventData = {
            event: 'purchase',
            order_id: orderId,
            ecommerce: {
                purchase: {
                    actionField: {
                        id: orderReference,
                        revenue: revenue.toFixed(2)
                    },
                    products: products
                }
            }
        };
        
        // Add userId if available
        if (userId) {
            eventData.userId = userId;
        }
        
        window.dataLayer.push(eventData);
    }
}

// Make globally available
window.ShuttleGAEvents = ShuttleGAEvents;
